import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Wallet, Download, FileText, Loader2, Calendar, TrendingDown, IndianRupee, Eye, EyeOff, ChevronDown, ChevronUp, PiggyBank } from 'lucide-react';
import { toast } from 'sonner';
import useSalaryVisibility from '../hooks/useSalaryVisibility';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MyPayslipsPage = () => {
  const { user } = useAuth();
  const [payslips, setPayslips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [downloading, setDownloading] = useState(null);
  const { showSalary, toggleSalary, mask } = useSalaryVisibility();
  // My salary structure (CTC)
  const [myStructure, setMyStructure] = useState(null);
  const [structureError, setStructureError] = useState(null);
  const [structureExpanded, setStructureExpanded] = useState(false);
  const isEmployeeView = user?.role === 'employee';

  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: 4 }, (_, i) => (currentYear - i).toString());

  const fetchPayslips = useCallback(async () => {
    setLoading(true);
    try {
      // Fetch all payslips for the selected year (all months)
      const months = Array.from({ length: 12 }, (_, i) => `${selectedYear}-${String(i + 1).padStart(2, '0')}`);
      const allPayslips = [];
      for (const month of months) {
        try {
          const res = await axios.get(`${API_URL}/api/payslips?month=${month}`, { withCredentials: true });
          if (res.data && res.data.length > 0) {
            allPayslips.push(...res.data);
          }
        } catch {
          // Skip months with no data
        }
      }
      // Sort by month descending
      allPayslips.sort((a, b) => b.month.localeCompare(a.month));
      setPayslips(allPayslips);
    } catch (err) {
      console.error('Failed to fetch payslips:', err);
    } finally {
      setLoading(false);
    }
  }, [selectedYear]);

  useEffect(() => { fetchPayslips(); }, [fetchPayslips]);

  // Fetch own salary structure (employees only; admin/HR use PayrollPage's dedicated tab)
  useEffect(() => {
    if (!isEmployeeView) return;
    axios.get(`${API_URL}/api/salary-structures/me`, { withCredentials: true })
      .then(res => setMyStructure(res.data))
      .catch(err => {
        setMyStructure(null);
        setStructureError(err.response?.data?.detail || 'Salary structure not available');
      });
  }, [isEmployeeView]);

  const handleDownload = async (payslipId, month) => {
    setDownloading(payslipId);
    try {
      const res = await axios.get(`${API_URL}/api/payslips/${payslipId}/pdf`, {
        withCredentials: true,
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `payslip_${month}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('Payslip downloaded');
    } catch (err) {
      toast.error('Failed to download payslip');
    } finally {
      setDownloading(null);
    }
  };

  const formatMonth = (monthStr) => {
    const [y, m] = monthStr.split('-');
    const months = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
    return `${months[parseInt(m) - 1]} ${y}`;
  };

  const formatCurrency = (amount) => `₹${Number(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 })}`;

  // Calculate yearly summary
  const yearSummary = {
    totalGross: payslips.reduce((s, p) => s + (p.gross_salary || 0), 0),
    totalNet: payslips.reduce((s, p) => s + (p.net_salary || 0), 0),
    totalTDS: payslips.reduce((s, p) => s + (p.tds || 0), 0),
    totalLOP: payslips.reduce((s, p) => s + (p.lop_deduction || 0), 0),
    months: payslips.length,
  };

  return (
    <div className="space-y-6" data-testid="my-payslips-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2" data-testid="my-payslips-title">
            <Wallet className="w-6 h-6 text-primary" />
            My Payslips
          </h1>
          <p className="text-sm text-muted-foreground mt-1">View and download your salary slips</p>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            onClick={toggleSalary}
            aria-label={showSalary ? 'Hide salary amounts' : 'Show salary amounts'}
            title={showSalary ? 'Hide salary amounts' : 'Show salary amounts'}
            data-testid="toggle-salary-visibility"
          >
            {showSalary ? <EyeOff className="w-3.5 h-3.5" /> : <Eye className="w-3.5 h-3.5" />}
            {showSalary ? 'Hide amounts' : 'Show amounts'}
          </Button>
          <Select value={selectedYear} onValueChange={setSelectedYear}>
            <SelectTrigger className="w-[140px]" data-testid="year-selector">
              <Calendar className="w-4 h-4 mr-1 text-muted-foreground" />
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Year Summary */}
      {payslips.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3" data-testid="payslip-summary">
          <Card className="border-l-4 border-l-blue-500">
            <CardContent className="p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Total Gross</p>
              <p className="text-lg font-bold mt-0.5" data-testid="summary-total-gross">{mask(formatCurrency(yearSummary.totalGross))}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-emerald-500">
            <CardContent className="p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Total Net</p>
              <p className="text-lg font-bold mt-0.5" data-testid="summary-total-net">{mask(formatCurrency(yearSummary.totalNet))}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-amber-500">
            <CardContent className="p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Total TDS</p>
              <p className="text-lg font-bold mt-0.5" data-testid="summary-total-tds">{mask(formatCurrency(yearSummary.totalTDS))}</p>
            </CardContent>
          </Card>
          <Card className={`border-l-4 ${yearSummary.totalLOP > 0 ? 'border-l-red-500' : 'border-l-slate-300'}`}>
            <CardContent className="p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">LOP Deductions</p>
              <p className="text-lg font-bold mt-0.5" data-testid="summary-total-lop">{mask(formatCurrency(yearSummary.totalLOP))}</p>
            </CardContent>
          </Card>
          <Card className="border-l-4 border-l-violet-500">
            <CardContent className="p-3">
              <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Payslips</p>
              <p className="text-lg font-bold mt-0.5">{yearSummary.months}</p>
            </CardContent>
          </Card>
        </div>
      )}

      {/* My CTC / Salary Structure (employees only) */}
      {isEmployeeView && (myStructure || structureError) && (
        <Card data-testid="my-ctc-card" className="border-l-4 border-l-primary">
          <CardHeader className="pb-3">
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <CardTitle className="text-base flex items-center gap-2">
                  <PiggyBank className="w-4 h-4 text-primary" />
                  My Salary Structure
                </CardTitle>
                <CardDescription className="text-xs mt-0.5">
                  Your current CTC breakdown — Basic, HRA, PF, Gratuity, and Take Home
                </CardDescription>
              </div>
              {myStructure && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setStructureExpanded(v => !v)}
                  className="gap-1 shrink-0"
                  data-testid="toggle-ctc-details"
                >
                  {structureExpanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
                  {structureExpanded ? 'Hide details' : 'Show details'}
                </Button>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {structureError && !myStructure ? (
              <p className="text-sm text-muted-foreground py-2" data-testid="ctc-error">{structureError}</p>
            ) : myStructure && (
              <>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Annual CTC</p>
                    <p className="text-lg font-bold" data-testid="ctc-annual">{mask(formatCurrency(myStructure.ctc))}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Monthly Gross</p>
                    <p className="text-lg font-bold" data-testid="ctc-monthly-gross">{mask(formatCurrency(myStructure.gross_salary))}</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Net Take Home</p>
                    <p className="text-lg font-bold text-emerald-600" data-testid="ctc-net">{mask(formatCurrency(myStructure.net_take_home ?? myStructure.net_salary))}</p>
                    <p className="text-[10px] text-muted-foreground mt-0.5">After TDS &amp; PF</p>
                  </div>
                  <div>
                    <p className="text-[10px] uppercase tracking-wider text-muted-foreground">Effective From</p>
                    <p className="text-sm font-semibold mt-1">{myStructure.effective_from || '—'}</p>
                  </div>
                </div>
                {structureExpanded && (
                  <div className="mt-4 pt-3 border-t space-y-3" data-testid="ctc-details">
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Monthly Earnings</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                        <span className="text-muted-foreground">Basic (40% CTC):</span>
                        <span className="font-mono col-span-2 sm:col-span-2" data-testid="ctc-basic">{mask(formatCurrency(myStructure.basic_salary))}</span>
                        <span className="text-muted-foreground">HRA (50% Basic):</span>
                        <span className="font-mono col-span-2 sm:col-span-2" data-testid="ctc-hra">{mask(formatCurrency(myStructure.hra))}</span>
                        <span className="text-muted-foreground">Special Allowance:</span>
                        <span className="font-mono col-span-2 sm:col-span-2" data-testid="ctc-special">{mask(formatCurrency(myStructure.special_allowance))}</span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Employer Contributions (Part of CTC)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                        <span className="text-muted-foreground">Employer PF (12% Basic):</span>
                        <span className="font-mono text-violet-600 col-span-2 sm:col-span-2" data-testid="ctc-employer-pf">{mask(formatCurrency(myStructure.employer_pf))}</span>
                        {myStructure.medical_insurance > 0 && (
                          <>
                            <span className="text-muted-foreground">Medical Insurance:</span>
                            <span className="font-mono text-violet-600 col-span-2 sm:col-span-2">{mask(formatCurrency(myStructure.medical_insurance))}</span>
                          </>
                        )}
                        {myStructure.gratuity_monthly > 0 && (
                          <>
                            <span className="text-muted-foreground">Gratuity ({(myStructure.gratuity_percent || 4.81).toFixed(2)}% Basic):</span>
                            <span className="font-mono text-violet-600 col-span-2 sm:col-span-2" data-testid="ctc-gratuity">
                              {mask(formatCurrency(myStructure.gratuity_monthly))}
                              <span className="text-[10px] text-muted-foreground ml-1">/mo · {mask(formatCurrency(myStructure.gratuity_annual))} p.a.</span>
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Monthly Deductions</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                        <span className="text-muted-foreground">Employee PF (12% Basic):</span>
                        <span className="font-mono text-destructive col-span-2 sm:col-span-2" data-testid="ctc-employee-pf">−{mask(formatCurrency(myStructure.employee_pf || myStructure.pf_contribution))}</span>
                        <span className="text-muted-foreground">Professional Tax:</span>
                        <span className="font-mono text-destructive col-span-2 sm:col-span-2">−{mask(formatCurrency(myStructure.professional_tax))}</span>
                        <span className="text-muted-foreground">Income Tax (TDS):</span>
                        <span className="font-mono text-destructive col-span-2 sm:col-span-2" data-testid="ctc-tds">
                          −{mask(formatCurrency(myStructure.monthly_tds || 0))}
                          <span className="text-[10px] text-muted-foreground ml-1">projected</span>
                        </span>
                      </div>
                    </div>
                    <div>
                      <p className="text-xs font-semibold uppercase text-muted-foreground mb-1.5">Income Tax Projection (New Regime · FY 2025-26)</p>
                      <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-xs">
                        <span className="text-muted-foreground">Annual Gross:</span>
                        <span className="font-mono col-span-2 sm:col-span-2" data-testid="tax-annual-gross">{mask(formatCurrency(myStructure.annual_gross))}</span>
                        <span className="text-muted-foreground">Standard Deduction:</span>
                        <span className="font-mono col-span-2 sm:col-span-2">−{mask(formatCurrency(myStructure.standard_deduction || 75000))}</span>
                        <span className="text-muted-foreground">Taxable Income:</span>
                        <span className="font-mono col-span-2 sm:col-span-2" data-testid="tax-taxable">{mask(formatCurrency(myStructure.annual_taxable_income))}</span>
                        <span className="text-muted-foreground">Annual Tax Liability:</span>
                        <span className="font-mono text-destructive col-span-2 sm:col-span-2" data-testid="tax-annual-liability">
                          {mask(formatCurrency(myStructure.annual_tax_liability || 0))}
                          {(myStructure.annual_taxable_income || 0) <= 1200000 && (myStructure.annual_tax_liability || 0) === 0 && (
                            <span className="text-[10px] text-emerald-600 ml-2">· Nil under §87A rebate</span>
                          )}
                        </span>
                        <span className="text-muted-foreground">Monthly TDS (current):</span>
                        <span className="font-mono text-destructive col-span-2 sm:col-span-2">−{mask(formatCurrency(myStructure.monthly_tds || 0))}</span>
                      </div>
                    </div>
                    <div className="border-t pt-2 space-y-1.5" data-testid="ctc-notes">
                      <p className="text-[10px] text-muted-foreground italic">
                        <b>a.</b> Gratuity is a long-term employee benefit fully funded by Wisewit Solutions Pvt Ltd. It is included in your overall CTC for cost representation purposes but is not deducted from your monthly take-home salary. Subject to eligibility under the Payment of Gratuity Act, 1972, the gratuity amount becomes payable upon separation from the company.
                      </p>
                      <p className="text-[10px] text-muted-foreground italic">
                        <b>b.</b> The TDS reflected in this statement is as per the New Tax Regime (Union Budget 2025) and may vary based on your taxable income and applicable tax regulations.
                      </p>
                      <p className="text-[10px] text-muted-foreground italic">
                        <b>c.</b> The actual monthly deductions may vary slightly based on bonuses, arrears, reimbursements, statutory revisions, or other one-time salary adjustments.
                      </p>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Payslips List */}
      {loading ? (
        <div className="flex items-center justify-center h-48" data-testid="loading-payslips">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
        </div>
      ) : payslips.length === 0 ? (
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 text-center">
            <FileText className="w-12 h-12 text-muted-foreground/30 mb-4" />
            <h3 className="text-lg font-semibold text-muted-foreground">No payslips for {selectedYear}</h3>
            <p className="text-sm text-muted-foreground mt-1">Payslips will appear here once generated by HR</p>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3" data-testid="payslips-list">
          {payslips.map((payslip) => (
            <Card key={payslip.id} className="hover:shadow-md transition-shadow" data-testid={`payslip-${payslip.month}`}>
              <CardContent className="p-4">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                  <div className="flex items-start gap-3">
                    <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                      <IndianRupee className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-sm">
                        {user?.role === 'admin' || user?.role === 'hr' ? (
                          <>{payslip.employee_name && <span className="text-primary">{payslip.employee_name} — </span>}{formatMonth(payslip.month)}</>
                        ) : formatMonth(payslip.month)}
                      </h3>
                      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 mt-1">
                        <span className="text-xs text-muted-foreground">
                          Gross: <span className="text-foreground font-medium" data-testid={`payslip-gross-${payslip.month}`}>{mask(formatCurrency(payslip.gross_salary))}</span>
                        </span>
                        <span className="text-xs text-muted-foreground">
                          Net: <span className="text-emerald-600 font-medium" data-testid={`payslip-net-${payslip.month}`}>{mask(formatCurrency(payslip.net_salary))}</span>
                        </span>
                        {payslip.lop_days > 0 && (
                          <Badge variant="destructive" className="text-[10px] gap-1">
                            <TrendingDown className="w-3 h-3" />
                            LOP: {payslip.lop_days}d ({mask(formatCurrency(payslip.lop_deduction))})
                          </Badge>
                        )}
                        {payslip.adjustments_total > 0 && (
                          <Badge className="text-[10px] gap-1 bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400 border-0" data-testid={`payslip-credit-${payslip.month}`}>
                            + {mask(formatCurrency(payslip.adjustments_total))} credit
                          </Badge>
                        )}
                      </div>
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 mt-1">
                        <span className="text-[10px] text-muted-foreground">PF: {mask(formatCurrency(payslip.pf_deduction))}</span>
                        <span className="text-[10px] text-muted-foreground">TDS: {mask(formatCurrency(payslip.tds))}</span>
                        <span className="text-[10px] text-muted-foreground">PT: {mask(formatCurrency(payslip.professional_tax))}</span>
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="gap-1.5 shrink-0"
                    onClick={() => handleDownload(payslip.id, payslip.month)}
                    disabled={downloading === payslip.id}
                    data-testid={`download-${payslip.month}`}
                  >
                    {downloading === payslip.id ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Download className="w-3.5 h-3.5" />
                    )}
                    Download PDF
                  </Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyPayslipsPage;
